import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  ManufacturerCreateSchema,
  ManufacturerQueryAllSchema,
  ManufacturerQuerySchema,
  ManufacturerUpdateSchema,
} from './schemas';

export function useGetManufacturers(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['MANUFACTURERS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<ManufacturerQueryAllSchema>({
        endpoint: '/api/v1/manufacturers',
        params: `${apiParamStr}&include=productsCount`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['MANUFACTURERS'] });
};

export const useGetManufacturersMutation: UseControlledMutation<ManufacturerQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['MANUFACTURERS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<ManufacturerQueryAllSchema>({
            endpoint: '/api/v1/manufacturers',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: ManufacturerQuerySchema }, ApiError, ManufacturerCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/manufacturers/`,
        payload,
      }),
  });

export function useGetManufacturer(id: string) {
  return useSuspenseQuery({
    queryKey: ['MANUFACTURER', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: ManufacturerQuerySchema }>({
        endpoint: `/api/v1/manufacturers/${id}`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: ManufacturerQuerySchema }, ApiError, Partial<ManufacturerUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/manufacturers/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['MANUFACTURER_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/manufacturers/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
